import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import {
    Badge,
    Collapse,
    Grow,
    List,
    Menu,
    MenuItem,
    Paper,
    Popper,
    useTheme,
} from "@material-ui/core";

import Dashboard from "@material-ui/icons/Dashboard";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import Contacts from "@material-ui/icons/Contacts";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import Help from "@material-ui/icons/Help";
import DeveloperModeIcon from "@material-ui/icons/DeveloperMode";
import Schedule from "@material-ui/icons/Schedule";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import AttachFile from "@material-ui/icons/AttachFile";
import DeviceHubOutlined from "@material-ui/icons/DeviceHubOutlined";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import RoomServiceIcon from "@material-ui/icons/RoomService";
import EventNoteIcon from "@material-ui/icons/EventNote";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import BarChartIcon from "@material-ui/icons/BarChart";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import {
    Brightness3,
    Brightness7,
    ExitToApp,
    FormatListBulletedSharp,
    LocalAtm,
    PeopleAltOutlined,
    Style,
    VolumeDown,
    VolumeUp,
} from "@material-ui/icons";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { socketConnection } from "../services/socket";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import usePlans from "../hooks/usePlans";

import ColorModeContext from "../layout/themeContext";
import NotificationsVolume from "../components/NotificationsVolume";

// ---------------------------------------------------------------------------
// Cores dos rótulos de seção (Opção B): um tom por grupo, aplicado no rótulo
// e num pequeno ponto antes do texto. Tons médios que funcionam tanto no
// tema claro quanto no escuro.
// ---------------------------------------------------------------------------
const sectionColors = {
    visaoGeral:    "#2D7BE8", // azul
    atendimento:   "#0891b2", // ciano
    agenda:        "#8b5cf6", // roxo
    financeiro:    "#d97706", // âmbar
    marketing:     "#db2777", // rosa
    administracao: "#16a34a", // verde
    sistema:       "#64748b", // ardósia
    suporte:       "#14b8a6", // teal
};

const useStyles = makeStyles((theme) => ({
    selected: {
        backgroundColor: theme.palette.type === "dark" ? "#333" : "#e4e8f4",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "#333" : "#e4e8f4",
        },
        borderLeft: theme.palette.type === "dark" ? "3px solid #00BFFF" : `3px solid ${theme.palette.primary.main}`,
    },

    footerTop: (props) => ({
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingRight: props.collapsed ? "11px" : "16px",
        paddingLeft: props.collapsed ? "11px" : "16px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
    }),

    footerBottom: {
        backgroundColor: theme.palette.type === "dark" ? "#2f3646" : "#f3f3f3",
        overflow: "hidden",
        borderRadius: 12,
        boxShadow: theme.palette.type === "dark"
            ? "0 8px 24px rgba(0,0,0,0.45)"
            : "0 8px 24px rgba(20,30,50,0.10)",
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
    },

    scrollableList: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        // Ícones de navegação um pouco menores, para um visual mais limpo.
        // Escopo restrito ao ícone dentro do ListItemIcon (não afeta as
        // setas de expandir/recolher, que ficam fora dele).
        "& .MuiListItemIcon-root .MuiSvgIcon-root": {
            fontSize: "1.3rem",
        },
        "&:hover": {
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[500],
            },
        },
        "&::-webkit-scrollbar": {
            width: "4px",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "transparent",
            borderRadius: "8px",
        },
    },

    bottomList: {
        flexShrink: 0,
        height: "auto",
    },

    listItemText: {
        fontSize: "0.9rem",
        fontWeight: 400,
    },

    nested: {
        paddingLeft: theme.spacing(4),
    },

    flyoutHeader: {
        padding: "10px 16px 6px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontSize: "0.65rem",
        color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
    },
    flyoutPaper: {
        minWidth: 210,
        marginLeft: 6,
        backgroundColor: theme.palette.type === "dark" ? "#2f3646" : "#fff",
        overflow: "hidden",
    },

    sectionLabel: {
        padding: "12px 16px 3px",
        display: "block",
        color: theme.palette.type === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.38)",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        fontSize: "0.65rem",
    },
    sectionDot: {
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        marginRight: 6,
        verticalAlign: "middle",
        position: "relative",
        top: -1,
    },

    userAvatar: {
        width: 32,
        height: 32,
        fontSize: "0.85rem",
        backgroundColor: theme.palette.primary.main,
    },
    userName: {
        fontWeight: 600,
        fontSize: "0.85rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    userEmail: {
        fontSize: "0.7rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
    },
}));

// ---------------------------------------------------------------------------
// ListItemLink — item de navegação com tooltip quando colapsado
// ---------------------------------------------------------------------------
function ListItemLink({ icon, primary, to, className, selected, onClick, collapsed, tourId, iconColor }) {
    const classes = useStyles();

    const renderLink = React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
    ));

    return (
        <li>
            <Tooltip title={collapsed ? primary : ""} placement="right" arrow>
                <ListItem
                    button
                    component={renderLink}
                    onClick={onClick}
                    className={`${className || ""} ${selected ? classes.selected : ""}`}
                    selected={selected}
                    data-tour={tourId || undefined}
                    style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        justifyContent: collapsed ? "center" : "flex-start",
                    }}
                >
                    {icon && (
                        <ListItemIcon style={{
                            minWidth: collapsed ? "unset" : 44,
                            justifyContent: "center",
                            color: iconColor || undefined,
                        }}>
                            {icon}
                        </ListItemIcon>
                    )}
                    {!collapsed && (
                        <ListItemText
                            primary={primary}
                            classes={{ primary: classes.listItemText }}
                        />
                    )}
                </ListItem>
            </Tooltip>
        </li>
    );
}

// ---------------------------------------------------------------------------
// SectionLabel — rótulo de grupo (oculto quando colapsado)
// ---------------------------------------------------------------------------
function SectionLabel({ children, collapsed, classes, color }) {
    if (collapsed) return <Divider style={{ margin: "4px 0" }} />;
    return (
        <li style={{ listStyle: "none" }}>
            <Typography
                variant="caption"
                className={classes.sectionLabel}
            >
                {color && (
                    <span
                        className={classes.sectionDot}
                        style={{ backgroundColor: color }}
                    />
                )}
                {children}
            </Typography>
        </li>
    );
}

// ---------------------------------------------------------------------------
// CollapsedFlyout — ícone com pop-up lateral dos sub-itens (menu colapsado)
// ---------------------------------------------------------------------------
function CollapsedFlyout({ icon, label, selected, items, location, drawerClose, iconColor }) {
    const classes = useStyles();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const closeTimer = useRef(null);
    const open = Boolean(anchorEl);

    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    };
    const handleOpen = (e) => {
        cancelClose();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setAnchorEl(null), 120);
    };
    const handleItemClick = () => {
        cancelClose();
        setAnchorEl(null);
        drawerClose();
    };

    useEffect(() => () => cancelClose(), []);

    return (
        <li style={{ listStyle: "none" }}>
            <Tooltip title="" placement="right" arrow>
                <ListItem
                    button
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                    className={selected ? classes.selected : ""}
                    selected={selected}
                    style={{ paddingTop: 6, paddingBottom: 6, justifyContent: "center" }}
                >
                    <ListItemIcon style={{ minWidth: "unset", justifyContent: "center", color: iconColor || undefined }}>
                        {icon}
                    </ListItemIcon>
                </ListItem>
            </Tooltip>
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="right-start"
                transition
                style={{ zIndex: theme.zIndex.drawer + 2 }}
                modifiers={{ preventOverflow: { enabled: true, boundariesElement: "viewport" } }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
                        <Paper
                            elevation={6}
                            className={classes.flyoutPaper}
                            onMouseEnter={cancelClose}
                            onMouseLeave={handleClose}
                        >
                            <div className={classes.flyoutHeader}>{label}</div>
                            <List disablePadding>
                                {items.map((item) => (
                                    <ListItemLink
                                        key={item.to}
                                        to={item.to}
                                        primary={item.primary}
                                        icon={item.icon}
                                        iconColor={iconColor}
                                        selected={location.pathname === item.to}
                                        onClick={handleItemClick}
                                        collapsed={false}
                                    />
                                ))}
                            </List>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </li>
    );
}

// ---------------------------------------------------------------------------
// Reducer de chats internos
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
    if (action.type === "LOAD_CHATS") {
        const chats = action.payload;
        const newChats = [];
        if (isArray(chats)) {
            chats.forEach((chat) => {
                const idx = state.findIndex((u) => u.id === chat.id);
                if (idx !== -1) state[idx] = chat;
                else newChats.push(chat);
            });
        }
        return [...state, ...newChats];
    }
    if (action.type === "UPDATE_CHATS") {
        const chat = action.payload;
        const idx = state.findIndex((u) => u.id === chat.id);
        if (idx !== -1) { state[idx] = chat; return [...state]; }
        return [chat, ...state];
    }
    if (action.type === "DELETE_CHAT") {
        const idx = state.findIndex((u) => u.id === action.payload);
        if (idx !== -1) state.splice(idx, 1);
        return [...state];
    }
    if (action.type === "RESET") return [];
    if (action.type === "CHANGE_CHAT") {
        return state.map((c) =>
            c.id === action.payload.chat.id ? action.payload.chat : c
        );
    }
    return state;
};

// ---------------------------------------------------------------------------
// MainListItems
// ---------------------------------------------------------------------------
const MainListItems = (props) => {
    const { drawerClose, collapsed, volume, setVolume, onOpenUserModal } = props;
    const classes = useStyles(props);
    const { whatsApps } = useContext(WhatsAppsContext);
    const [anchorElAccount, setAnchorElAccount] = useState(null);
    const { user, handleLogout } = useContext(AuthContext);
    const { colorMode } = useContext(ColorModeContext);
    const [connectionWarning, setConnectionWarning] = useState(false);
    const [showCampaigns, setShowCampaigns] = useState(false);
    const [showKanban, setShowKanban] = useState(false);
    const [showOpenAi, setShowOpenAi] = useState(false);
    const [showIntegrations, setShowIntegrations] = useState(false);
    const [showSchedules, setShowSchedules] = useState(false);
    const [showInternalChat, setShowInternalChat] = useState(false);
    const [showExternalApi, setShowExternalApi] = useState(false);
    const [invisible, setInvisible] = useState(true);
    const [pageNumber] = useState(1);
    const [searchParam] = useState("");
    const [chats, dispatch] = useReducer(reducer, []);
    const { getPlanCompany } = usePlans();
    const location = useLocation();
    const theme = useTheme();

    useEffect(() => {
        async function fetchData() {
            const planConfigs = await getPlanCompany(undefined, user.companyId);
            setShowCampaigns(planConfigs.plan.useCampaigns);
            setShowKanban(planConfigs.plan.useKanban);
            setShowOpenAi(planConfigs.plan.useOpenAi);
            setShowIntegrations(planConfigs.plan.useIntegrations);
            setShowSchedules(planConfigs.plan.useSchedules);
            setShowInternalChat(planConfigs.plan.useInternalChat);
            setShowExternalApi(planConfigs.plan.useExternalApi);
        }
        fetchData();
    }, [user.companyId, getPlanCompany]);

    useEffect(() => {
        const t = setTimeout(() => fetchChats(), 500);
        return () => clearTimeout(t);
    }, [searchParam, pageNumber]); // eslint-disable-line

    useEffect(() => {
        const companyId = localStorage.getItem("companyId");
        const socket = socketConnection({ companyId });
        socket.on(`company-${companyId}-chat`, (data) => {
            if (data.action === "new-message" || data.action === "update") {
                dispatch({ type: "CHANGE_CHAT", payload: data });
            }
        });
        return () => socket.disconnect();
    }, []);

    useEffect(() => {
        let count = 0;
        if (chats.length > 0) {
            chats.forEach((chat) => {
                chat.users.forEach((cu) => {
                    if (cu.userId === user.id) count += cu.unreads;
                });
            });
        }
        setInvisible(count <= 0);
    }, [chats, user.id]);

    useEffect(() => {
        if (localStorage.getItem("cshow")) setShowCampaigns(true);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            if (whatsApps.length > 0) {
                const offline = whatsApps.filter((w) =>
                    ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
                );
                setConnectionWarning(offline.length > 0);
            }
        }, 2000);
        return () => clearTimeout(t);
    }, [whatsApps]);

    const fetchChats = async () => {
        try {
            const { data } = await api.get("/chats/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_CHATS", payload: data.records });
        } catch (err) {
            toastError(err);
        }
    };

    const handleClickLogout = () => handleLogout();

    const toggleColorMode = () => colorMode.toggleColorMode();

    const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "?";

    // Bloco de Atendimento — exibido para todos os perfis
    const atendimentoItems = (
        <>
            <ListItemLink
                to="/tickets"
                primary={i18n.t("mainDrawer.listItems.tickets")}
                icon={<WhatsAppIcon />}
                iconColor={sectionColors.atendimento}
                selected={location.pathname.startsWith("/tickets")}
                onClick={drawerClose}
                collapsed={collapsed}
                tourId="menu-tickets"
            />
            <ListItemLink
                to="/contacts"
                primary={i18n.t("mainDrawer.listItems.contacts")}
                icon={<Contacts />}
                iconColor={sectionColors.atendimento}
                selected={location.pathname === "/contacts"}
                onClick={drawerClose}
                collapsed={collapsed}
            />
            {showKanban && (
                <ListItemLink
                    to="/kanban"
                    primary="Kanban"
                    icon={<Style />}
                    iconColor={sectionColors.atendimento}
                    selected={location.pathname === "/kanban"}
                    onClick={drawerClose}
                    collapsed={collapsed}
                />
            )}
            <ListItemLink
                to="/todolist"
                primary={i18n.t("Tarefas")}
                icon={<FormatListBulletedSharp />}
                iconColor={sectionColors.atendimento}
                selected={location.pathname === "/todolist"}
                onClick={drawerClose}
                collapsed={collapsed}
            />
            {showInternalChat && (
                <ListItemLink
                    to="/chats"
                    primary={i18n.t("mainDrawer.listItems.chats")}
                    icon={
                        <Badge color="secondary" variant="dot" invisible={invisible}>
                            <ForumIcon />
                        </Badge>
                    }
                    iconColor={sectionColors.atendimento}
                    selected={location.pathname === "/chats"}
                    onClick={drawerClose}
                    collapsed={collapsed}
                />
            )}
            <ListItemLink
                to="/quick-messages"
                primary={i18n.t("mainDrawer.listItems.quickMessages")}
                icon={<FlashOnIcon />}
                iconColor={sectionColors.atendimento}
                selected={location.pathname === "/quick-messages"}
                onClick={drawerClose}
                collapsed={collapsed}
            />
            <ListItemLink
                to="/tags"
                primary={i18n.t("mainDrawer.listItems.tags")}
                icon={<LocalOfferIcon />}
                iconColor={sectionColors.atendimento}
                selected={location.pathname === "/tags"}
                onClick={drawerClose}
                collapsed={collapsed}
            />
        </>
    );

    const agendaSubmenu = (
        <AgendaSubmenu
            showSchedules={showSchedules}
            location={location}
            drawerClose={drawerClose}
            collapsed={collapsed}
        />
    );

    return (
        <div className={classes.footerTop}>
            <div className={classes.footerBottom}>
                <List className={classes.scrollableList} disablePadding>

                    {/* ── VISÃO GERAL (admin only) ── */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.visaoGeral}>
                                    Visão Geral
                                </SectionLabel>
                                <ListItemLink
                                    to="/"
                                    primary="Dashboard"
                                    icon={<Dashboard />}
                                    iconColor={sectionColors.visaoGeral}
                                    selected={location.pathname === "/"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                            </>
                        )}
                    />

                    {/* ── ATENDIMENTO ── */}
                    <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.atendimento}>
                        Atendimento
                    </SectionLabel>
                    {atendimentoItems}

                    {/* ── AGENDA ── */}
                    {agendaSubmenu}

                    {/* ── FINANCEIRO (admin only) ── */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.financeiro}>
                                    Financeiro
                                </SectionLabel>
                                <ListItemLink
                                    to="/financeiro"
                                    primary={i18n.t("mainDrawer.listItems.financeiro")}
                                    icon={<LocalAtm />}
                                    iconColor={sectionColors.financeiro}
                                    selected={location.pathname === "/financeiro"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                            </>
                        )}
                    />

                    {/* ── MARKETING (admin only) ── */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            showCampaigns ? (
                                <>
                                    <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.marketing}>
                                        Marketing
                                    </SectionLabel>
                                    <CampaignSubmenu
                                        showCampaigns={showCampaigns}
                                        location={location}
                                        drawerClose={drawerClose}
                                        collapsed={collapsed}
                                    />
                                </>
                            ) : null
                        )}
                    />

                    {/* ── ADMINISTRAÇÃO (admin only) ── */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.administracao}>
                                    Administração
                                </SectionLabel>
                                <ListItemLink
                                    to="/connections"
                                    primary={i18n.t("mainDrawer.listItems.connections")}
                                    icon={
                                        <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                                            <SyncAltIcon />
                                        </Badge>
                                    }
                                    iconColor={sectionColors.administracao}
                                    selected={location.pathname === "/connections"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                    tourId="menu-connections"
                                />
                                <ListItemLink
                                    to="/queues"
                                    primary={i18n.t("mainDrawer.listItems.queues")}
                                    icon={<AccountTreeOutlinedIcon />}
                                    iconColor={sectionColors.administracao}
                                    selected={location.pathname === "/queues"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                    tourId="menu-queues"
                                />
                                <ListItemLink
                                    to="/users"
                                    primary={i18n.t("mainDrawer.listItems.users")}
                                    icon={<PeopleAltOutlined />}
                                    iconColor={sectionColors.administracao}
                                    selected={location.pathname === "/users"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                    tourId="menu-users"
                                />
                                <ListItemLink
                                    to="/services"
                                    primary="Serviços"
                                    icon={<RoomServiceIcon />}
                                    iconColor={sectionColors.administracao}
                                    selected={location.pathname === "/services"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                <ListItemLink
                                    to="/files"
                                    primary={i18n.t("mainDrawer.listItems.files")}
                                    icon={<AttachFile />}
                                    iconColor={sectionColors.administracao}
                                    selected={location.pathname === "/files"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {showOpenAi && (
                                    <ListItemLink
                                        to="/prompts"
                                        primary={i18n.t("mainDrawer.listItems.prompts")}
                                        icon={<EmojiObjectsIcon />}
                                        iconColor={sectionColors.administracao}
                                        selected={location.pathname === "/prompts"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showIntegrations && (
                                    <ListItemLink
                                        to="/queue-integration"
                                        primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                                        icon={<DeviceHubOutlined />}
                                        iconColor={sectionColors.administracao}
                                        selected={location.pathname === "/queue-integration"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                                {showExternalApi && (
                                    <ListItemLink
                                        to="/messages-api"
                                        primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                                        icon={<DeveloperModeIcon />}
                                        iconColor={sectionColors.administracao}
                                        selected={location.pathname === "/messages-api"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                            </>
                        )}
                    />

                    {/* ── SISTEMA (admin only) ── */}
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.sistema}>
                                    Sistema
                                </SectionLabel>
                                <ListItemLink
                                    to="/settings"
                                    primary={i18n.t("mainDrawer.listItems.settings")}
                                    icon={<SettingsOutlinedIcon />}
                                    iconColor={sectionColors.sistema}
                                    selected={location.pathname === "/settings"}
                                    onClick={drawerClose}
                                    collapsed={collapsed}
                                />
                                {user.super && (
                                    <ListItemLink
                                        to="/announcements"
                                        primary={i18n.t("mainDrawer.listItems.annoucements")}
                                        icon={<AnnouncementIcon />}
                                        iconColor={sectionColors.sistema}
                                        selected={location.pathname === "/announcements"}
                                        onClick={drawerClose}
                                        collapsed={collapsed}
                                    />
                                )}
                            </>
                        )}
                    />

                    {/* ── SUPORTE (todos) ── */}
                    <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.suporte}>
                        Suporte
                    </SectionLabel>
                    <ListItemLink
                        to="/helps"
                        primary={i18n.t("mainDrawer.listItems.helps")}
                        icon={<Help />}
                        iconColor={sectionColors.suporte}
                        selected={location.pathname === "/helps"}
                        onClick={drawerClose}
                        collapsed={collapsed}
                    />

                </List>

                <Divider />

                {/* Rodapé fixo: conta do usuário + sair */}
                <List className={classes.bottomList} disablePadding>
                    <Tooltip title={collapsed ? "Minha conta" : ""} placement="right" arrow>
                        <ListItem button onClick={(e) => setAnchorElAccount(e.currentTarget)} style={{ justifyContent: collapsed ? "center" : "flex-start" }}>
                            <ListItemAvatar style={{ minWidth: collapsed ? "unset" : 56 }}>
                                <Avatar className={classes.userAvatar}>{userInitial}</Avatar>
                            </ListItemAvatar>
                            {!collapsed && (
                                <ListItemText
                                    primary={
                                        <Typography className={classes.userName} noWrap>
                                            {user?.name || "Minha conta"}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography className={classes.userEmail} noWrap>
                                            {user?.email || ""}
                                        </Typography>
                                    }
                                    disableTypography
                                />
                            )}
                        </ListItem>
                    </Tooltip>

                    <Tooltip title={collapsed ? "Sair" : ""} placement="right" arrow>
                        <ListItem button onClick={handleClickLogout} style={{
                            paddingTop: 6,
                            paddingBottom: 6,
                            justifyContent: collapsed ? "center" : "flex-start",
                        }}>
                            <ListItemIcon style={{
                                minWidth: collapsed ? "unset" : 44,
                                justifyContent: "center",
                            }}>
                                <ExitToApp />
                            </ListItemIcon>
                            {!collapsed && (
                                <ListItemText
                                    primary="Sair"
                                    classes={{ primary: classes.listItemText }}
                                />
                            )}
                        </ListItem>
                    </Tooltip>
                </List>
            </div>

            {/* Menu de contexto da conta */}
            <Menu
                id="account-menu"
                anchorEl={anchorElAccount}
                open={Boolean(anchorElAccount)}
                onClose={() => setAnchorElAccount(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ style: { maxHeight: 320, width: 230 } }}
            >
                <MenuItem
                    onClick={() => {
                        // O modal em si vive fora do Drawer (levantado para o
                        // layout), pois no mobile o Drawer é "temporary" e
                        // desmonta os próprios filhos ao fechar — abrir e já
                        // fechar o drawer aqui fechava o modal junto.
                        onOpenUserModal();
                        setAnchorElAccount(null);
                        drawerClose();
                    }}
                >
                    <ListItemIcon><PeopleAltOutlined /></ListItemIcon>
                    <ListItemText primary="Perfil" />
                </MenuItem>

                <MenuItem onClick={toggleColorMode}>
                    <ListItemIcon>
                        {theme.palette.type === "dark" ? <Brightness3 /> : <Brightness7 />}
                    </ListItemIcon>
                    <ListItemText primary="Modo Escuro" />
                </MenuItem>

                <MenuItem
                    style={{ cursor: "default" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", padding: "4px 0" }}>
                        <ListItemText primary="Volume das Notificações" style={{ marginBottom: 4 }} />
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <VolumeDown style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                            <NotificationsVolume volume={volume} onChange={setVolume} />
                            <VolumeUp style={{ marginLeft: 8, color: theme.palette.text.secondary }} />
                        </div>
                    </div>
                </MenuItem>
            </Menu>
        </div>
    );
};

// ---------------------------------------------------------------------------
// AgendaSubmenu — submenu colapsável de agenda
// ---------------------------------------------------------------------------
function AgendaSubmenu({ showSchedules, location, drawerClose, collapsed }) {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    const items = [
        showSchedules && {
            to: "/schedules",
            primary: i18n.t("mainDrawer.listItems.schedules"),
            icon: <Schedule />,
        },
        { to: "/appointments", primary: "Agendamentos", icon: <EventNoteIcon /> },
        { to: "/schedule-blocks", primary: "Bloqueios", icon: <EventBusyIcon /> },
        { to: "/appointment-dashboard", primary: "Dashboard", icon: <BarChartIcon /> },
    ].filter(Boolean);

    const isAgendaActive = items.some((i) => location.pathname === i.to);

    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    };

    return (
        <>
            <SectionLabel collapsed={collapsed} classes={classes} color={sectionColors.agenda}>
                Agenda
            </SectionLabel>
            {collapsed ? (
                <CollapsedFlyout
                    icon={<CalendarTodayIcon />}
                    label="Agenda"
                    selected={isAgendaActive}
                    items={items}
                    location={location}
                    drawerClose={drawerClose}
                    iconColor={sectionColors.agenda}
                />
            ) : (
                <>
                    <ListItem
                        button
                        onClick={handleToggle}
                        style={{ paddingTop: 6, paddingBottom: 6 }}
                    >
                        <ListItemIcon style={{ minWidth: 44, justifyContent: "center", color: sectionColors.agenda }}>
                            <CalendarTodayIcon />
                        </ListItemIcon>
                        <ListItemText primary="Agenda" classes={{ primary: classes.listItemText }} />
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {items.map((item) => (
                                <ListItemLink
                                    key={item.to}
                                    to={item.to}
                                    primary={item.primary}
                                    icon={item.icon}
                                    iconColor={sectionColors.agenda}
                                    selected={location.pathname === item.to}
                                    onClick={drawerClose}
                                    className={classes.nested}
                                    collapsed={false}
                                />
                            ))}
                        </List>
                    </Collapse>
                </>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// CampaignSubmenu — submenu colapsável de campanhas
// ---------------------------------------------------------------------------
function CampaignSubmenu({ showCampaigns, location, drawerClose, collapsed }) {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    if (!showCampaigns) return null;

    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    };

    return (
        <>
            <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""} placement="right" arrow>
                <ListItem button onClick={handleToggle} style={{
                    paddingTop: 6,
                    paddingBottom: 6,
                    justifyContent: collapsed ? "center" : "flex-start",
                }}>
                    <ListItemIcon style={{
                        minWidth: collapsed ? "unset" : 44,
                        justifyContent: "center",
                        color: sectionColors.marketing,
                    }}>
                        <EventAvailableIcon />
                    </ListItemIcon>
                    {!collapsed && (
                        <ListItemText primary={i18n.t("mainDrawer.listItems.campaigns")} classes={{ primary: classes.listItemText }} />
                    )}
                    {!collapsed && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItem>
            </Tooltip>
            <Collapse in={open && !collapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <ListItemLink
                        to="/campaigns"
                        primary="Listagem"
                        icon={<ListIcon />}
                        iconColor={sectionColors.marketing}
                        selected={location.pathname === "/campaigns"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                    <ListItemLink
                        to="/contact-lists"
                        primary="Listas de Contatos"
                        icon={<PeopleIcon />}
                        iconColor={sectionColors.marketing}
                        selected={location.pathname === "/contact-lists"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                    <ListItemLink
                        to="/campaigns-config"
                        primary="Configurações"
                        icon={<SettingsOutlinedIcon />}
                        iconColor={sectionColors.marketing}
                        selected={location.pathname === "/campaigns-config"}
                        onClick={drawerClose}
                        className={classes.nested}
                        collapsed={false}
                    />
                </List>
            </Collapse>
        </>
    );
}

export default MainListItems;
